import classNames from 'classnames';
import { AnimatePresence, domAnimation, LazyMotion, m } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable';
import { useSelector } from 'react-redux';
import { EventsState } from '../../../store/types';
import { ColorKeys, TextOverrides, } from '../../../types/working-model';
import { parseColor } from '../../../utils/document-head';
import { OptionalComponent } from '../../../utils/optional-component';
import { useRefCallback } from '../../../utils/use-ref-callback';
import EditSectionColorsModal, { EContentColorOverrideNames } from '../edit-section-colors/edit-section-colors-modal';
import Icon, { COLORS, ICONS } from '../icon';
import './editable-text.scss';

interface Props {
	value: string;
	onChange: (value: string) => void; //handles text changes
	style?: any;
	className?: string;
	elementClass?: string;

	tagType?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";//creates the specified tag and removes markdown, more tag types can be added

	elementName?: EContentColorOverrideNames; //name of the element being edited (description/title), used in the color editor modal

	//Your onOverrideChange func should update the styling_override attribute of the module you're editing
	//and include pathing to the specific elemnt you're editing
	//e.g.
	// {
	// 	...module,
	// 	styling_overrides: {
	// 		...module.styling_overrides,
	// 		[your element e.g "title"]: override
	// 	}
	// }
	onOverrideChange?: (override: TextOverrides) => void;
	// that path is also what you would pass in as the initial textOverrides
	colorOverride?: string;

	//include these defaults if you want to give the user the option of editing an override without setting one before hand. 
	//The default should represet what the template would be (title is "h1"/"h2" and "headingTextcolor", most things are left aligned etc.)
	defaultColor?: ColorKeys;
	disabled?: boolean;
	maxCharacters?: number;
	displayCountAtXCharacter?: number;
}

function convertFromHtml(html: string): string {
	const tempCont = document.createElement('div');
	tempCont.innerHTML = html;
	return tempCont.innerText;
}

const EditableTextSlim: React.FC<Props> = (props) => {
	const {
		CreateEventReducer: {
			workingEvent
		}
	} = useSelector((state: EventsState) => state);
	const { colorOverride, onOverrideChange, defaultColor, elementName, tagType = "h1", value, disabled, elementClass = "", maxCharacters, displayCountAtXCharacter = 0 } = props;

	const colorPalette = workingEvent?.settings.design.colors.colors;
	const color = colorOverride?.split("-")[0]; //removing the "-color" from the classname
	const [text, setText] = useState(value);
	const [colorsOpen, setColorsOpen] = useState(false);
	const [toolBarActive, setToolBarActive] = useState(false);

	const editorRef = useRef<HTMLDivElement | null>(null);

	// keep in sync with parent
	useEffect(() => {
		setText(value);
	}, [value]);

	// Use Refcallback per https://github.com/lovasoa/react-contenteditable/issues/161#issuecomment-736053428
	const handleTextChange = useRefCallback((event: ContentEditableEvent) => {
		setText(event?.target.value);
	}, []);

	const handleKeyDown = useRefCallback((event: any) => {
		const isNotBackspace = event.key !== 'Backspace' || event.code !== 'Backspace' || event.keyCode !== 8;

		if (maxCharacters && convertFromHtml(text).length >= maxCharacters && isNotBackspace) {
			event.preventDefault();
		}
	});

	// Use Refcallback per https://github.com/lovasoa/react-contenteditable/issues/161#issuecomment-736053428
	const handleBlur = useRefCallback(() => {
		if (convertFromHtml(text) !== value) { // Only update if changed
			props.onChange(convertFromHtml(text));
		}
		//We need to give the onClick function time to fire otherwise the onBlur fires first and removes the toolbar before it has a chance to fire the onClick.
		setTimeout(() => setToolBarActive(false), 300);
	}, [text]);

	function handleColorChange(color: string) {
		if (!onOverrideChange) return;
		onOverrideChange({
			color
		});
	}

	const startingColor = color || defaultColor;
	const currentColor: [string, number] | undefined = startingColor ? colorPalette?.[startingColor] : ['#444', 1];

	function openColors() {
		setColorsOpen(true);
	}

	return (
		<div
			style={props.style ?? {}}
			className={classNames("editable-text slim-container", { disabled }, props.className, { active: toolBarActive })}
		>
			<ContentEditable
				innerRef={editorRef}
				html={text || ''}
				onBlur={handleBlur}
				onChange={handleTextChange}
				onKeyDown={handleKeyDown}
				onFocus={() => setToolBarActive(true)}
				tagName={tagType}
				className={classNames(elementClass, colorOverride)}
			/>

			<button
				className="round edit-text-button"
				onClick={() => editorRef.current?.focus()}
				disabled={disabled}
			>
				<Icon name={ICONS.EDIT} size={12} color={COLORS.WHITE} />
			</button>

			{startingColor ? (
				<div className="toolbar slim">
					<button
						className={classNames("small round color-display")}
						style={{
							backgroundColor: currentColor ? parseColor(currentColor) : "",
						}}
						onClick={openColors}
					/>
				</div>
			) : null}

			<EditSectionColorsModal
				open={colorsOpen}
				onClose={() => setColorsOpen(false)}
				onFinish={handleColorChange}
				title={elementName || EContentColorOverrideNames.title}
			/>
			<AnimatePresence>
				<LazyMotion features={domAnimation}>
					<OptionalComponent display={!!maxCharacters && toolBarActive && convertFromHtml(text).length >= displayCountAtXCharacter}>
						<m.span
							className="max-character-count"
							initial={{ scale: 0, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0, opacity: 0 }}
							transition={{
								type: "spring",
								stiffness: 900,
								damping: 15,
								velocity: 1.1
							}}
						>{convertFromHtml(text).length}/{maxCharacters}</m.span>
					</OptionalComponent>
				</LazyMotion>
			</AnimatePresence>
		</div >
	);
};

export default EditableTextSlim;
